/**
 * Lazy platform-administrator member list fetch for one system.
 */

import { useEffect, useRef, useState } from "react";
import {
  fetchPlatformAdministratorMembers,
  type PlatformAdministratorMemberRow,
  type PlatformAdministratorMembersClientResult,
} from "../api/cognitoryPlatformAdminMembersClient";

export type PlatformAdministratorMembersPhase =
  | "idle"
  | "loading"
  | "listed"
  | "empty"
  | "truncated"
  | "unauthenticated"
  | "forbidden"
  | "invalid_request"
  | "temporary_failure"
  | "unable_to_verify";

function mapListedPhase(input: {
  truncated: boolean;
  memberCount: number;
}): PlatformAdministratorMembersPhase {
  if (input.truncated) {
    return "truncated";
  }
  if (input.memberCount === 0) {
    return "empty";
  }
  return "listed";
}

function mapDenialPhase(
  kind: Exclude<PlatformAdministratorMembersClientResult["kind"], "listed">,
): PlatformAdministratorMembersPhase {
  switch (kind) {
    case "unauthenticated":
      return "unauthenticated";
    case "forbidden":
      return "forbidden";
    case "invalid_request":
      return "invalid_request";
    case "temporary_failure":
      return "temporary_failure";
    case "unable_to_verify":
      return "unable_to_verify";
  }
}

export function usePlatformAdministratorMembers(input: {
  systemId: string;
  enabled: boolean;
}) {
  const [phase, setPhase] = useState<PlatformAdministratorMembersPhase>("idle");
  const [members, setMembers] = useState<PlatformAdministratorMemberRow[]>([]);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [fetchCount, setFetchCount] = useState(0);
  const requestGenerationRef = useRef(0);

  useEffect(() => {
    if (!input.enabled) {
      requestGenerationRef.current += 1;
      setPhase("idle");
      setMembers([]);
      setRequestId(null);
      return;
    }

    requestGenerationRef.current += 1;
    const generation = requestGenerationRef.current;
    setPhase("loading");
    setFetchCount(count => count + 1);
    const systemId = input.systemId;

    void (async () => {
      const result = await fetchPlatformAdministratorMembers({ systemId });
      if (generation !== requestGenerationRef.current) {
        return;
      }

      setRequestId(result.requestId);

      if (result.kind === "listed") {
        setPhase(
          mapListedPhase({
            truncated: result.truncated,
            memberCount: result.members.length,
          }),
        );
        setMembers(result.members);
        return;
      }

      setMembers([]);
      setPhase(mapDenialPhase(result.kind));
    })();

    return () => {
      requestGenerationRef.current += 1;
    };
  }, [input.enabled, input.systemId]);

  return {
    phase,
    members,
    requestId,
    fetchCount,
  };
}
